import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type User, type LoginCredentials } from '../types';
import { authService } from '../services/authService';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => void;
  setAuth: (token: string, user: User, refreshToken?: string) => void;
  clearAuth: () => void;
  restoreFromImpersonation: (token: string, user: User) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true });
        try {
          const response = await authService.login(credentials);
          console.log('Login response:', response.data);
          const { token, refreshToken, user: rawUser, features } = response.data.data as any;
          // Feature flags ride beside the user in the login payload; keep them on the user so
          // every screen reads them from one place (lib/features.ts).
          const user = features ? { ...rawUser, features } : rawUser;
          
          localStorage.setItem('token', token);
          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
          }
          
          set({ 
            token, 
            refreshToken,
            user, 
            isAuthenticated: true, 
            isLoading: false 
          });
          
          return user;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth-storage');
        set({ 
          user: null, 
          token: null, 
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false
        });
      },

      setAuth: (token: string, user: User, refreshToken?: string) => {
        localStorage.setItem('token', token);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
        
        set({ 
          token, 
          refreshToken,
          user, 
          isAuthenticated: true,
          isLoading: false
        });
      },

      clearAuth: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth-storage');
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false
        });
      },

      restoreFromImpersonation: (token: string, user: User) => {
        localStorage.setItem('token', token);
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        token: state.token, 
        refreshToken: state.refreshToken,
        user: state.user, 
        isAuthenticated: state.isAuthenticated 
      }),
      onRehydrateStorage: () => (state) => {
        console.log('Rehydrating auth state:', state);
        // Ensure consistency: if no user, set isAuthenticated to false
        if (state && !state.user && state.isAuthenticated) {
          console.log('Fixing inconsistent auth state - no user but authenticated');
          state.isAuthenticated = false;
          localStorage.removeItem('token');
        }
      },
    }
  )
); 